import React, { useState } from 'react';
import { useSearchAdminStorefrontProductsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontProductView } from '../../models/StorefrontProducts';
import GridComposer from '../GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../models/GridConfiguration';
import Badge from '../Common/Badge';
import { IconButton, PrimaryButton, ICommandBarItemProps } from '@fluentui/react';
import DetailsModal from '../DetailsModal/DetailsModal';
import CreateProductPromoDialog from './CreateProductPromoDialog';
import AddToPromotionDialog from './AddToPromotionDialog';
import useCan, { PRODUCT_PROMOS_ADMIN } from '../../permissions/ability';
import SuggestedProductsStrip from './SuggestedProductsStrip';

interface StorefrontProductsAdminTableProps {
    storefront: string;
}

const StorefrontProductsAdminTable: React.FC<StorefrontProductsAdminTableProps> = ({ storefront }) => {
    // UX gate only. The API re-checks both the permission and the org entitlement, so hiding the button
    // is convenience rather than enforcement.
    const canManagePromos = useCan(PRODUCT_PROMOS_ADMIN);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [filter, setFilter] = useState<string>();
    const [filterField, setFilterField] = useState<string>();
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<string>();
    const [selectedProduct, setSelectedProduct] = useState<IStorefrontProductView | undefined>();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [addToPromoOpen, setAddToPromoOpen] = useState<boolean>(false);
    const [promoOpen, setPromoOpen] = useState<boolean>(false);

    const conditions = { page, pageSize, searchTerm: filter, searchBy: filterField, sortBy, sortDirection };
    const productsQuery = useSearchAdminStorefrontProductsQuery(
        { storefrontId: storefront, conditions },
        { skip: !storefront }
    );

    const handleDismiss = () => setSelectedProduct(undefined);

    // The catalog is a property of the products themselves, not of the storefront, and promos are
    // applied per catalog — so a mixed-catalog selection is refused rather than silently promoting only
    // part of it. Selection survives paging, so catalogs are remembered as rows are seen rather than
    // read off the current page: otherwise selecting on page 1 and paging on would lose them.
    const catalogsById = React.useRef<Record<string, string>>({});
    React.useEffect(() => {
        for (const item of productsQuery.data?.items ?? []) {
            if (item.id && item.catalog) catalogsById.current[item.id] = item.catalog;
        }
    }, [productsQuery.data?.items]);

    const selectedCatalogs = Array.from(new Set(
        selectedIds.map(id => catalogsById.current[id]).filter((c): c is string => !!c)
    ));

    const promoCommands: ICommandBarItemProps[] = canManagePromos ? [{
        key: 'createPromo',
        text: selectedIds.length > 0 ? `Create Promo (${selectedIds.length})` : 'Create Promo',
        iconProps: { iconName: 'Tag' },
        disabled: selectedIds.length === 0 || selectedCatalogs.length !== 1,
        title: selectedIds.length === 0
            ? 'Select one or more products to put a promo on.'
            : selectedCatalogs.length > 1
                ? 'Selected products span more than one catalog. Promos are applied per catalog.'
                : undefined,
        onClick: () => setPromoOpen(true),
    }, {
        key: 'addToPromo',
        text: selectedIds.length > 0 ? `Add to Promotion (${selectedIds.length})` : 'Add to Promotion',
        iconProps: { iconName: 'Add' },
        // Same catalog constraint as Create Promo: a campaign belongs to one catalog, so a mixed
        // selection is refused rather than silently adding only part of it.
        disabled: selectedIds.length === 0 || selectedCatalogs.length !== 1,
        title: selectedIds.length === 0
            ? 'Select one or more products to add to an existing promotion.'
            : selectedCatalogs.length > 1
                ? 'Selected products span more than one catalog. Promotions are applied per catalog.'
                : undefined,
        onClick: () => setAddToPromoOpen(true),
    }] : [];

    const getCustomColumns = (): IColumnDetails[] => [
        {
            index: 0,
            identifier: 'details',
            label: '',
            type: '',
            component: (product: IStorefrontProductView) => (
                <IconButton onClick={() => setSelectedProduct(product)} iconProps={{ iconName: 'View' }} title="Details" ariaLabel="Details" />
            ),
            required: true,
        },
        {
            identifier: 'inStock',
            label: 'In Stock',
            type: 'Boolean',
            component: (product: IStorefrontProductView) => (
                <Badge
                    text={product.inStock ? 'In Stock' : 'Out of Stock'}
                    color={product.inStock ? 'success' : 'error'}
                />
            ),
            required: false,
        },
    ];

    return (
        <div style={{ overflowX: 'auto' }}>
            {/* Renders nothing unless the organization has sales history to suggest from, so the tab is
                unchanged for everyone else. The grid carries no cache tags, so a product listed from the
                strip only shows up here because of this refetch. */}
            <SuggestedProductsStrip storefront={storefront} onProductsAdded={() => productsQuery.refetch()} />

            <GridComposer
                data={productsQuery.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={productsQuery.isLoading || productsQuery.isFetching}
                onRefresh={() => productsQuery.refetch()}
                selectionMode={canManagePromos ? GridSelectionMode.multiple : GridSelectionMode.none}
                // Selection is controlled: DataGrid builds each new selection by appending to (or
                // filtering) the list it is given, so without passing it back the selection could never
                // hold more than the row just clicked.
                selectedItems={selectedIds}
                onSelectedItemsChange={setSelectedIds}
                additionalCommands={promoCommands}
                columnSetName={'StorefrontProduct'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column);
                    setSortDirection(direction);
                }}
                onFilter={(f, column) => {
                    setFilter(f);
                    setFilterField(column);
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={true}
                getItemId={(item: IStorefrontProductView) => item.id}
            />

            {selectedProduct && (
                <DetailsModal
                    product={selectedProduct.id}
                    catalog={selectedProduct.catalog ?? ''}
                    open={true}
                    onClose={handleDismiss}
                />
            )}

            {addToPromoOpen && selectedCatalogs.length === 1 && (
                <AddToPromotionDialog
                    catalog={selectedCatalogs[0]}
                    storefront={storefront}
                    productIds={selectedIds}
                    onDismiss={() => setAddToPromoOpen(false)}
                    onApplied={() => {
                        setAddToPromoOpen(false);
                        productsQuery.refetch();
                    }}
                />
            )}

            {promoOpen && selectedCatalogs.length === 1 && (
                <CreateProductPromoDialog
                    catalog={selectedCatalogs[0]}
                    storefront={storefront}
                    productIds={selectedIds}
                    onDismiss={() => setPromoOpen(false)}
                    onApplied={() => {
                        setPromoOpen(false);
                        productsQuery.refetch();
                    }}
                />
            )}
        </div>
    );
};

export default StorefrontProductsAdminTable;
